import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { clearAuthSession, getStoredAuth, persistAuthSession } from '../utils/authStorage';
import { fetchCurrentUser, logoutUser } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState(() => {
    const storedAuth = getStoredAuth();
    return {
      accessToken: storedAuth?.accessToken || '',
      user: storedAuth?.user || null,
      expiresIn: storedAuth?.expiresIn || null,
      tokenType: storedAuth?.tokenType || 'bearer',
      isAuthenticated: Boolean(storedAuth?.accessToken),
      isInitialized: false,
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const resetAuthState = () => {
      queryClient.clear();
      setAuthState({
        accessToken: '',
        user: null,
        expiresIn: null,
        tokenType: 'bearer',
        isAuthenticated: false,
        isInitialized: true,
      });
    };

    const handleStorageChange = (event) => {
      if (event.key === 'vidya_kasam_auth' && !event.newValue) {
        resetAuthState();
      }
    };

    const handleSessionEnded = async (event) => {
      const message = event?.detail?.message || '';
      const sessionContext = event?.detail?.sessionContext || null;
      const activeSessionLabel = sessionContext?.active_session_label || '';
      const popupMessage = activeSessionLabel
        ? `${message} Active session: ${activeSessionLabel}.`
        : message;

      if (message.includes('Your session has been ended because a newer login is active')) {
        await Swal.fire({
          title: 'Session Ended',
          text: popupMessage,
          icon: 'warning',
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: 'signin-force-login-confirm',
            popup: 'signin-force-login-popup',
            title: 'signin-force-login-title',
            htmlContainer: 'signin-force-login-text',
            actions: 'signin-force-login-actions',
          },
          buttonsStyling: false,
        });
      }

      resetAuthState();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('vidya-kasam:auth-session-ended', handleSessionEnded);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('vidya-kasam:auth-session-ended', handleSessionEnded);
    };
  }, [queryClient]);

  useEffect(() => {
    let isMounted = true;

    async function hydrateUser() {
      if (!authState.accessToken) {
        if (isMounted) {
          setAuthState((currentState) => ({
            ...currentState,
            isInitialized: true,
          }));
        }
        return;
      }

      try {
        const currentUser = await fetchCurrentUser();
        if (!isMounted) {
          return;
        }

        setAuthState((currentState) => {
          const nextState = {
            ...currentState,
            user: currentUser,
            isAuthenticated: true,
            isInitialized: true,
          };

          persistAuthSession(nextState);
          return nextState;
        });
      } catch (error) {
        clearAuthSession();
        queryClient.clear();
        if (!isMounted) {
          return;
        }

        setAuthState({
          accessToken: '',
          user: null,
          expiresIn: null,
          tokenType: 'bearer',
          isAuthenticated: false,
          isInitialized: true,
        });
      }
    }

    hydrateUser();

    return () => {
      isMounted = false;
    };
  }, [authState.accessToken, queryClient]);

  const login = ({ accessToken, user, expiresIn, tokenType = 'bearer' }) => {
    queryClient.clear();

    const nextState = {
      accessToken,
      user,
      expiresIn: expiresIn || null,
      tokenType,
      isAuthenticated: Boolean(accessToken),
      isInitialized: true,
    };

    persistAuthSession(nextState);
    setAuthState(nextState);
  };

  const logout = async () => {
    try {
      if (authState.accessToken) {
        await logoutUser();
      }
    } catch (error) {
      // Ignore logout request failures and clear the local session anyway.
    } finally {
      clearAuthSession();
      queryClient.clear();
      setAuthState({
        accessToken: '',
        user: null,
        expiresIn: null,
        tokenType: 'bearer',
        isAuthenticated: false,
        isInitialized: true,
      });
    }
  };

  const updateUser = (user) => {
    setAuthState((currentState) => {
      const nextState = {
        ...currentState,
        user,
      };

      persistAuthSession(nextState);
      return nextState;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
